import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Use relative path to work with both Vite dev proxy and nginx production proxy
const API_BASE_URL = '/api/v1';

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      const currentPath = window.location.pathname;

      // Don't try to refresh on auth pages
      const isAuthPage = currentPath === '/login' || currentPath === '/register';
      if (isAuthPage) {
        return Promise.reject(error);
      }

      // Check if this is a TV app
      const isTvApp = currentPath.startsWith('/tv/');

      // Don't try to refresh if it's the refresh endpoint itself
      if (originalRequest.url === '/auth/refresh') {
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem('refreshToken');

      // No refresh token - handle based on app type
      if (!refreshToken) {
        if (isTvApp) {
          // TV app: dispatch event to show QR code
          window.dispatchEvent(new CustomEvent('api-401-error', { detail: { path: currentPath } }));
        } else {
          // Regular app: redirect to login
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Save new tokens
        localStorage.setItem('authToken', access_token);
        localStorage.setItem('refreshToken', newRefreshToken);

        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        // Process queued requests
        processQueue(null, access_token);

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear tokens and handle based on app type
        processQueue(refreshError as Error, null);

        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');

        if (isTvApp) {
          // TV app: dispatch event to show QR code
          window.dispatchEvent(new CustomEvent('api-401-error', { detail: { path: currentPath } }));
        } else {
          // Regular app: redirect to login
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
